import { reflect } from '@effector/reflect';
import { Anchor, Loader, NumberFormatter, Table } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { devicePagePlayersModel } from 'pages/device-page/model';
import { $t, translated } from 'shared/lib';
import { playerRoute } from 'shared/routing';
import { MultiSelect, SearchInput } from 'shared/ui';
import cls from './device-page-players.module.scss';

const NameFilter = reflect({
	view: SearchInput<'input'>,
	bind: {
		'data-test-id': 'device-page-players-name-filter',
		label: translated('player-registry.filters.name.label'),
		placeholder: translated('player-registry.filters.name.placeholder'),
		className: cls.devicePagePlayersFilter,
		value: devicePagePlayersModel.filters.name.$value,
		onChange: (e) => devicePagePlayersModel.filters.name.setValue(e.target.value),
	},
});

const CurrenciesFilter = reflect({
	view: MultiSelect,
	bind: {
		'data-test-id': 'device-page-players-currencies-filter',
		className: cls.devicePagePlayersFilter,
		data: devicePagePlayersModel.$currencies,
		value: devicePagePlayersModel.filters.currencies.$value,
		onChange: (value) => devicePagePlayersModel.filters.currencies.setValue(value),
		label: translated('player-registry.filters.currencies.label'),
		placeholder: translated('player-registry.filters.currencies.placeholder'),
		nothingFoundMessage: translated('player-registry.filters.currencies.empty'),
		searchable: true,
		clearable: true,
	},
});

const StatusesFilter = reflect({
	view: MultiSelect,
	bind: {
		'data-test-id': 'device-page-players-statuses-filter',
		className: cls.devicePagePlayersFilter,
		data: $t.map((t) => [
			{ value: 'true', label: t('player-registry.status.active') },
			{ value: 'false', label: t('player-registry.status.not-active') },
		]),
		value: devicePagePlayersModel.filters.isConnected.$value.map((values) => values.map(String)),
		onChange: (value) =>
			devicePagePlayersModel.filters.isConnected.setValue(value.map((item) => item === 'true')),
		label: translated('player-registry.filters.statuses.label'),
		placeholder: translated('player-registry.filters.statuses.placeholder'),
		nothingFoundMessage: translated('player-registry.filters.statuses.empty'),
		clearable: true,
	},
});

export const DevicePagePlayers = () => {
	const { t } = useTranslation();

	const [players, pending] = useUnit([
		devicePagePlayersModel.$players,
		devicePagePlayersModel.$playersPending,
	]);

	return (
		<div className={cls.devicePagePlayers}>
			<div className={cls.devicePagePlayersFilters}>
				<NameFilter />
				<CurrenciesFilter />
				<StatusesFilter />
			</div>
			{pending ? (
				<Loader />
			) : (
				<Table>
					<Table.Thead>
						<Table.Tr>
							<Table.Th>{t('player-registry.table-columns.name')}</Table.Th>
							<Table.Th>{t('player-registry.table-columns.currency')}</Table.Th>
							<Table.Th>{t('player-registry.table-columns.balance')}</Table.Th>
							<Table.Th>{t('player-registry.table-columns.status')}</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{players.map((player) => (
							<Table.Tr key={player.id}>
								<Table.Td>
									<Anchor
										data-test-id={`device-page-player-link-${player.id}`}
										component={Link<StoreValue<typeof playerRoute.$params>>}
										to={playerRoute}
										params={{ id: player.id }}
									>
										{player.name}
									</Anchor>
								</Table.Td>
								<Table.Td>{player.currency}</Table.Td>
								<Table.Td>
									<NumberFormatter value={player.balance} />
								</Table.Td>
								<Table.Td>
									{player.isConnected
										? t('player-registry.status.active')
										: t('player-registry.status.not-active')}
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			)}
		</div>
	);
};
